/**
 * @file Petit menu contextuel (appui long sur une arme / un emplacement) positionne au-dessus de sa cible, ferme au clic exterieur.
 */
import { h } from '../../core/dom';

export interface MenuItem {
  label: string;
  onSelect: () => void;
  disabled?: boolean;
}

export class PopupMenu {
  private el: HTMLElement | null = null;
  private off: (() => void) | null = null;

  constructor(private readonly host: HTMLElement) {}

  get isOpen(): boolean {
    return this.el !== null;
  }

  open(anchor: HTMLElement, items: readonly MenuItem[]): void {
    this.close();
    const el = h(
      'div',
      { class: 'popup-menu panel', role: 'menu', 'data-ui-block': true },
      ...items.map((it) =>
        h(
          'button',
          {
            class: `btn small${it.disabled ? ' disabled' : ''}`,
            type: 'button',
            role: 'menuitem',
            onclick: () => {
              this.close();
              if (!it.disabled) it.onSelect();
            },
          },
          it.label,
        ),
      ),
    );
    this.host.append(el);
    // Position dans le repere de l'hote : au-dessus de la cible si la place manque en dessous.
    const hostBox = this.host.getBoundingClientRect();
    const box = anchor.getBoundingClientRect();
    const w = el.offsetWidth;
    const hgt = el.offsetHeight;
    const left = Math.max(6, Math.min(hostBox.width - w - 6, box.left - hostBox.left + box.width / 2 - w / 2));
    const below = box.bottom - hostBox.top + 6;
    const top = below + hgt > hostBox.height ? box.top - hostBox.top - hgt - 6 : below;
    el.style.left = `${left.toFixed(0)}px`;
    el.style.top = `${Math.max(6, top).toFixed(0)}px`;
    this.el = el;
    const onDown = (e: PointerEvent): void => {
      if (e.target instanceof Node && el.contains(e.target)) return;
      this.close();
    };
    // Differe d'un tour : l'appui long qui ouvre le menu ne doit pas le refermer a son relachement.
    const timer = window.setTimeout(() => document.addEventListener('pointerdown', onDown, true), 0);
    this.off = () => {
      window.clearTimeout(timer);
      document.removeEventListener('pointerdown', onDown, true);
    };
  }

  close(): void {
    this.off?.();
    this.off = null;
    this.el?.remove();
    this.el = null;
  }
}

/**
 * Appui long (souris ou doigt) sur `el` : appelle `onLong` apres `ms` sans deplacement notable. Retourne le nettoyage.
 * Le clic contextuel (clic droit) ouvre aussi le menu.
 */
export const attachLongPress = (el: HTMLElement, ms: number, onLong: () => void): (() => void) => {
  let timer: number | null = null;
  let sx = 0;
  let sy = 0;
  const cancel = (): void => {
    if (timer !== null) window.clearTimeout(timer);
    timer = null;
  };
  const down = (e: PointerEvent): void => {
    cancel();
    sx = e.clientX;
    sy = e.clientY;
    timer = window.setTimeout(() => {
      timer = null;
      onLong();
    }, ms);
  };
  const move = (e: PointerEvent): void => {
    if (timer !== null && Math.hypot(e.clientX - sx, e.clientY - sy) > 10) cancel();
  };
  const ctx = (e: Event): void => {
    e.preventDefault();
    cancel();
    onLong();
  };
  el.addEventListener('pointerdown', down);
  el.addEventListener('pointermove', move);
  el.addEventListener('pointerup', cancel);
  el.addEventListener('pointercancel', cancel);
  el.addEventListener('pointerleave', cancel);
  el.addEventListener('contextmenu', ctx);
  return () => {
    cancel();
    el.removeEventListener('pointerdown', down);
    el.removeEventListener('pointermove', move);
    el.removeEventListener('pointerup', cancel);
    el.removeEventListener('pointercancel', cancel);
    el.removeEventListener('pointerleave', cancel);
    el.removeEventListener('contextmenu', ctx);
  };
};
